/**
 * COMPONENTE STORICO ATTIVITÀ
 *
 * Visualizza lo storico delle attività degli utenti in un modale fullscreen.
 * Supporta filtri avanzati, paginazione e visualizzazione dettagliata.
 *
 * FUNZIONALITÀ:
 * - Visualizzazione attività con permessi basati su ruolo utente
 * - Filtri per data, tipo azione, email utente (solo admin)
 * - Paginazione lato client
 * - Espansione righe per dettagli completi
 * - Icone colorate per diversi tipi di azioni
 *
 * PROPS:
 * - open: boolean - controlla apertura/chiusura modale
 * - onClose: function - callback per chiusura modale
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableBody,
  TableCell,
  Button,
  Loader,
  Pagination,
  Form,
  Dropdown,
  Icon,
  Message,
} from "semantic-ui-react";
import { activityAPI } from "../api";
import { isUserAdmin } from "../utils/userCurrentlyLogged";

// === CONFIGURAZIONE TIPI DI AZIONI ===
// Opzioni per il dropdown di filtro tipo azione
const actionTypeOptions = [
  { key: "", value: "", text: "Tutte le azioni" },
  { key: "LOGIN", value: "LOGIN", text: "Login" },
  { key: "LOGOUT", value: "LOGOUT", text: "Logout" },
  {
    key: "CREATE_OPERATOR",
    value: "CREATE_OPERATOR",
    text: "Creazione Operatore",
  },
  {
    key: "UPDATE_OPERATOR",
    value: "UPDATE_OPERATOR",
    text: "Modifica Operatore",
  },
  {
    key: "DELETE_OPERATOR",
    value: "DELETE_OPERATOR",
    text: "Elimina Operatore",
  },
  {
    key: "SEARCH_OPERATORS",
    value: "SEARCH_OPERATORS",
    text: "Ricerca Operatori",
  },
];

export default function StoricoAttivita({ open, onClose }) {
  // === STATI DEL COMPONENTE ===
  const [activities, setActivities] = useState([]); // Lista delle attività
  const [loading, setLoading] = useState(false); // Stato caricamento
  const [currentPage, setCurrentPage] = useState(1); // Pagina corrente
  const [totalPages, setTotalPages] = useState(0); // Totale pagine
  const [totalActivities, setTotalActivities] = useState(0); // Totale attività
  const [expandedRow, setExpandedRow] = useState(null); // Riga espansa per dettagli
  const [isAdmin, setIsAdmin] = useState(false); // Permessi utente
  const [error, setError] = useState(""); // Messaggi di errore

  // Stato dei filtri di ricerca
  const [filters, setFilters] = useState({
    user_email: "", // Filtro email utente (solo admin)
    action_type: "", // Filtro tipo azione
    date_from: "", // Filtro data inizio
    date_to: "", // Filtro data fine
  });

  // === EFFETTI ===
  // Carica le attività quando il modale si apre o cambia pagina
  useEffect(() => {
    if (open) {
      setIsAdmin(isUserAdmin());
      fetchActivities();
    }
  }, [open, currentPage]);

  // === FUNZIONI PRINCIPALI ===

  /**
   * Carica le attività dal server applicando filtri e paginazione
   */
  const fetchActivities = async () => {
    setLoading(true);
    setError("");

    try {
      // Rimuove filtri vuoti prima di inviare la richiesta
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== "")
      );

      const data = await activityAPI.getActivities(
        currentPage,
        20, // Elementi per pagina
        cleanFilters
      );

      if (data.success) {
        setActivities(data.activities);
        setTotalActivities(data.total);
        setTotalPages(Math.ceil(data.total / data.limit));
        setIsAdmin(data.isAdmin); // Aggiorna permessi dal server
      } else {
        setError(data.message || "Errore nel caricamento delle attività");
      }
    } catch (error) {
      console.error("Errore nel caricamento delle attività:", error);
      setError(error.message || "Errore nel caricamento dell attività");
    } finally {
      setLoading(false);
    }
  };

  // === GESTORI EVENTI ===

  /**
   * Gestisce i cambiamenti nei filtri di ricerca
   */
  const handleFilterChange = (e, { name, value }) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Esegue la ricerca con i filtri correnti
   */
  const handleSearch = () => {
    setCurrentPage(1); // Torna alla prima pagina
    fetchActivities();
  };

  /**
   * Resetta tutti i filtri e ricarica i dati
   */
  const handleReset = () => {
    setFilters({
      user_email: "",
      action_type: "",
      date_from: "",
      date_to: "",
    });
    setCurrentPage(1);
    // Piccolo delay per permettere l'aggiornamento dello stato
    setTimeout(() => {
      fetchActivities();
    }, 100);
  };

  // === FUNZIONI UTILITY ===

  /**
   * Formatta una data per la visualizzazione in formato italiano
   */
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  /**
   * Ottiene l'etichetta leggibile per un tipo di azione
   */
  const getActionLabel = (actionType) => {
    const option = actionTypeOptions.find((opt) => opt.value === actionType);
    return option ? option.text : actionType;
  };

  /**
   * Ottiene l'icona appropriata per un tipo di azione
   */
  const getActionIcon = (actionType) => {
    switch (actionType) {
      case "LOGIN":
        return "sign in";
      case "LOGOUT":
        return "sign out";
      case "CREATE_OPERATOR":
        return "plus";
      case "UPDATE_OPERATOR":
        return "edit";
      case "DELETE_OPERATOR":
        return "trash";
      case "SEARCH_OPERATORS":
        return "search";
      default:
        return "info";
    }
  };

  /**
   * Ottiene il colore appropriato per un tipo di azione
   */
  const getActionColor = (actionType) => {
    switch (actionType) {
      case "LOGIN":
        return "green";
      case "LOGOUT":
        return "orange";
      case "CREATE_OPERATOR":
        return "blue";
      case "UPDATE_OPERATOR":
        return "yellow";
      case "DELETE_OPERATOR":
        return "red";
      case "SEARCH_OPERATORS":
        return "purple";
      default:
        return "grey";
    }
  };

  /**
   * Gestisce l'espansione/contrazione delle righe per mostrare i dettagli
   */
  const toggleRowExpansion = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  /**
   * Renderizza i dettagli dell'attività in formato JSON leggibile
   */
  const renderDetails = (details) => {
    if (!details) return "N/A";

    try {
      // Prova a parsare come JSON se è una stringa
      const parsed =
        typeof details === "string" ? JSON.parse(details) : details;

      return (
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "10px",
            borderRadius: "4px",
            fontSize: "12px",
            fontFamily: "monospace",
          }}
        >
          <pre
            style={{
              margin: 0,
              maxHeight: "200px",
              overflow: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {JSON.stringify(parsed, null, 2)}
          </pre>
        </div>
      );
    } catch {
      // Se il parsing fallisce, mostra come testo semplice
      return (
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "10px",
            borderRadius: "4px",
            fontSize: "12px",
          }}
        >
          {details.toString()}
        </div>
      );
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="fullscreen">
      <Modal.Header>
        <Icon name="history" />
        Storico Attività {isAdmin ? "(Tutte le attività)" : "(Le mie attività)"}
      </Modal.Header>

      <Modal.Content scrolling>
        {/* Filtri */}
        <Form
          style={{
            marginBottom: "20px",
            padding: "15px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
          }}
        >
          <Form.Group widths="equal">
            {isAdmin && (
              <Form.Input
                label="Email Utente"
                name="user_email"
                value={filters.user_email}
                onChange={handleFilterChange}
                placeholder="Cerca per email..."
                icon="user"
              />
            )}
            <Form.Field>
              <label>Tipo Azione</label>
              <Dropdown
                selection
                name="action_type"
                value={filters.action_type}
                onChange={handleFilterChange}
                options={actionTypeOptions}
                placeholder="Seleziona tipo azione..."
              />
            </Form.Field>
            <Form.Input
              label="Data Da"
              name="date_from"
              type="date"
              value={filters.date_from}
              onChange={handleFilterChange}
            />
            <Form.Input
              label="Data A"
              name="date_to"
              type="date"
              value={filters.date_to}
              onChange={handleFilterChange}
            />
          </Form.Group>
          <Form.Group>
            <Button
              primary
              onClick={handleSearch}
              icon="search"
              content="Cerca"
            />
            <Button onClick={handleReset} icon="refresh" content="Reset" />
          </Form.Group>
        </Form>

        {/* Messaggio di errore */}
        {error && (
          <Message negative>
            <Message.Header>Errore</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {/* Risultati */}
        <div style={{ marginBottom: "15px" }}>
          <strong>Totale attività: {totalActivities}</strong>
          {totalActivities > 0 && (
            <span style={{ marginLeft: "20px", color: "#666" }}>
              Pagina {currentPage} di {totalPages}
            </span>
          )}
        </div>

        {loading ? (
          <Loader active size="large">
            Caricamento attività...
          </Loader>
        ) : (
          <>
            {activities.length === 0 ? (
              <Message info>
                <Message.Header>Nessuna attività trovata</Message.Header>
                <p>
                  Non ci sono attività che corrispondono ai criteri di ricerca.
                </p>
              </Message>
            ) : (
              <Table celled striped>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell width={3}>Data/Ora</TableHeaderCell>
                    {isAdmin && (
                      <TableHeaderCell width={2}>Utente</TableHeaderCell>
                    )}
                    <TableHeaderCell width={2}>Azione</TableHeaderCell>
                    <TableHeaderCell width={2}>Target</TableHeaderCell>
                    <TableHeaderCell width={2}>IP Address</TableHeaderCell>
                    <TableHeaderCell width={1}>Dettagli</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activities.map((activity) => (
                    <React.Fragment key={activity.id}>
                      <TableRow>
                        <TableCell>
                          <div style={{ fontSize: "13px" }}>
                            {formatDate(activity.created_at)}
                          </div>
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <Icon name="user" size="small" />
                              {activity.user_email}
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Icon
                              name={getActionIcon(activity.action_type)}
                              color={getActionColor(activity.action_type)}
                              size="small"
                            />
                            <span style={{ marginLeft: "8px" }}>
                              {getActionLabel(activity.action_type)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {activity.target_type && activity.target_id
                            ? `${activity.target_type} #${activity.target_id}`
                            : activity.target_type || "N/A"}
                        </TableCell>
                        <TableCell>
                          <code style={{ fontSize: "11px" }}>
                            {activity.ip_address || "N/A"}
                          </code>
                        </TableCell>
                        <TableCell textAlign="center">
                          <Button
                            size="mini"
                            icon={
                              expandedRow === activity.id ? "eye slash" : "eye"
                            }
                            onClick={() => toggleRowExpansion(activity.id)}
                            title={
                              expandedRow === activity.id
                                ? "Nascondi dettagli"
                                : "Mostra dettagli"
                            }
                          />
                        </TableCell>
                      </TableRow>
                      {expandedRow === activity.id && (
                        <TableRow>
                          <TableCell colSpan={isAdmin ? 6 : 5}>
                            <div style={{ padding: "15px" }}>
                              <div style={{ marginBottom: "10px" }}>
                                <strong>Dettagli dell'attività</strong>
                              </div>
                              {renderDetails(activity.details)}
                              <div
                                style={{
                                  marginTop: "10px",
                                  fontSize: "11px",
                                  color: "#666",
                                }}
                              >
                                <strong>User Agent:</strong>{" "}
                                {activity.user_agent || "N/A"}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}

            {totalPages > 1 && (
              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <Pagination
                  activePage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(e, { activePage }) =>
                    setCurrentPage(activePage)
                  }
                  boundaryRange={1}
                  siblingRange={1}
                  ellipsisItem={{ content: "...", icon: "true" }}
                  firstItem={{ content: "«", icon: true }}
                  lastItem={{ content: "»", icon: true }}
                  prevItem={{ content: "‹", icon: true }}
                  nextItem={{ content: "›", icon: true }}
                />
              </div>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} icon="close" content="Chiudi" />
      </Modal.Actions>
    </Modal>
  );
}
